import React, { useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import { AuthContext } from './context/AuthContext';

import Navbar from './components/Navbar';
import Footer from './components/Footer';

import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrdersPage from './pages/OrdersPage';
import LoginPage from './pages/LoginPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import StoreInfoPage from './pages/StoreInfoPage';
import AboutPage from './pages/AboutPage';
import CustomOrdersPage from './pages/CustomOrdersPage';
import WishlistPage from './pages/WishlistPage';
import TermsPage from './pages/TermsPage';
import SellOnHunarPage from './pages/SellOnHunarPage';
import ReviewsPage from './pages/ReviewsPage';

import AdminLayout from './components/admin/AdminLayout';
import DashboardPage from './pages/admin/DashboardPage';
import AdminProductsPage from './pages/admin/AdminProductsPage';
import AdminOrderPage from './pages/admin/AdminOrderPage';
import AdminCategoriesPage from './pages/admin/AdminCategoriesPage';
import AdminOrdersPage from './pages/admin/AdminOrdersPage';
import AdminHomepageCMS from './pages/admin/AdminHomepageCMS';
import AdminAboutPage from './pages/admin/AdminAboutPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminUploadsPage from './pages/admin/AdminUploadsPage';
import AdminSellerApplicationsPage from './pages/admin/AdminSellerApplicationsPage';
import AdminPromosPage from './pages/admin/AdminPromosPage';
import AdminReviewsPage from './pages/admin/AdminReviewsPage';

import './theme.css';

const roleRank = { customer: 0, editor: 1, admin: 2, superadmin: 3 };

function RequireRole({ minimumRole, children }) {
  const { user, loading } = useContext(AuthContext);
  const userRole = String(user?.role || '').toLowerCase();

  if (loading) {
    return <div className="container py-5 text-center" role="status">Loading access…</div>;
  }

  if ((roleRank[userRole] || 0) < roleRank[minimumRole]) {
    return <Navigate to="/shop" replace />;
  }

  return children;
}

function RequireAuth({ children }) {
  const { user, loading } = useContext(AuthContext);
  const location = useLocation();

  if (loading) {
    return <div className="container py-5 text-center" role="status">Checking your sign-in…</div>;
  }

  if (!user) {
    const redirect = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
  }

  return children;
}

function RequireTerms({ children }) {
  const { user, loading } = useContext(AuthContext);
  const location = useLocation();
  const termsExempt = ['/login', '/auth/callback'].includes(location.pathname);
  const termsAccepted = user?.tos_accepted === true
    || user?.tosAccepted === true
    || Boolean(user?.tosAcceptedAt);

  if (loading || !user || termsExempt || termsAccepted) {
    return children;
  }

  return <TermsPage />;
}

function AdminIndexGuard() {
  const { user } = useContext(AuthContext);
  const userRole = String(user?.role || '').toLowerCase();
  if (userRole === 'editor') {
    return <Navigate to="/admin/products" replace />;
  }
  return <RequireRole minimumRole="admin"><DashboardPage /></RequireRole>;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
            <Router>
            <div className="d-flex flex-column min-vh-100">
              <Navbar />
              <main className="flex-grow-1">
                <RequireTerms>
                  <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/shop" element={<ShopPage />} />
                  <Route path="/product/:slug" element={<ProductDetailPage />} />
                  <Route path="/cart" element={<CartPage />} />
                  <Route path="/wishlist" element={<RequireAuth><WishlistPage /></RequireAuth>} />
                  <Route path="/checkout" element={<RequireAuth><CheckoutPage /></RequireAuth>} />
                  <Route path="/orders" element={<OrdersPage />} />
                  <Route path="/reviews" element={<ReviewsPage />} />
                  <Route path="/custom-orders" element={<CustomOrdersPage />} />
                  <Route path="/custom-orders/:id" element={<CustomOrdersPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/auth/callback" element={<AuthCallbackPage />} />
                  <Route path="/shipping" element={<StoreInfoPage type="shipping" />} />
                  <Route path="/returns" element={<StoreInfoPage type="returns" />} />
                  <Route path="/help" element={<StoreInfoPage type="help" />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/sell-on-hunar" element={<SellOnHunarPage />} />

                  {/* Admin Protected Nested Routes */}
                  <Route path="/admin" element={<RequireRole minimumRole="editor"><AdminLayout /></RequireRole>}>
                    <Route index element={<AdminIndexGuard />} />
                    <Route path="products" element={<RequireRole minimumRole="editor"><AdminProductsPage /></RequireRole>} />
                    <Route path="product-order" element={<RequireRole minimumRole="editor"><AdminOrderPage /></RequireRole>} />
                    <Route path="order" element={<Navigate to="/admin/product-order" replace />} />
                    <Route path="categories" element={<RequireRole minimumRole="admin"><AdminCategoriesPage /></RequireRole>} />
                    <Route path="orders" element={<RequireRole minimumRole="admin"><AdminOrdersPage /></RequireRole>} />
                    <Route path="custom-orders" element={<RequireRole minimumRole="admin"><CustomOrdersPage adminView /></RequireRole>} />
                    <Route path="custom-orders/:id" element={<RequireRole minimumRole="admin"><CustomOrdersPage adminView /></RequireRole>} />
                    <Route path="reviews" element={<RequireRole minimumRole="admin"><AdminReviewsPage /></RequireRole>} />
                    <Route path="promos" element={<RequireRole minimumRole="superadmin"><AdminPromosPage /></RequireRole>} />
                    <Route path="cms" element={<RequireRole minimumRole="admin"><AdminHomepageCMS /></RequireRole>} />
                    <Route path="about" element={<RequireRole minimumRole="admin"><AdminAboutPage /></RequireRole>} />
                    <Route path="settings" element={<RequireRole minimumRole="superadmin"><AdminSettingsPage /></RequireRole>} />
                    <Route path="users" element={<RequireRole minimumRole="superadmin"><AdminUsersPage /></RequireRole>} />
                    <Route path="uploads" element={<RequireRole minimumRole="editor"><AdminUploadsPage /></RequireRole>} />
                    <Route path="seller-applications" element={<RequireRole minimumRole="admin"><AdminSellerApplicationsPage /></RequireRole>} />
                  </Route>

                  <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </RequireTerms>
              </main>
              <Footer />
            </div>
            </Router>
          </WishlistProvider>
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
